import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Search, X, HelpCircle } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import api from '../../services/api';
import Badge from '../../components/shared/Badge';

const STATUS_LABELS = { ativo: 'Ativo', morno: 'Morno', parado: 'Parado', novo: 'Novo' };
const ESTAGIO_FUNIL_LABELS = {
  novo_contato: 'Novo Contato', contato_feito: 'Contato Feito', nao_respondeu: 'Não Respondeu',
  convite_enviado: 'Convite Enviado', visita_agendada: 'Visita Agendada', visita_realizada: 'Visita Realizada',
  parceria_ativa: 'Parceria Ativa', em_recontato: 'Em Recontato', inativo: 'Inativo',
};
const FUNIL_LABELS = { ativo: 'Ativos', visita: 'Visita agendada', proposta: 'Proposta', reserva: 'Reserva', venda: 'Venda' };
const FUNIL_NOTES = {
  proposta: 'Contagem inclui apenas propostas com corretor vinculado (corretor_id), disponível a partir ' +
    'de 20/07/2026 — propostas anteriores a essa data não entram nessa contagem.',
  reserva: 'Contagem inclui apenas propostas com corretor vinculado (corretor_id) e ainda em status ' +
    'pendente, em análise ou aprovada, disponível a partir de 20/07/2026 — propostas anteriores a essa ' +
    'data não entram nessa contagem.',
};

export default function ParceirosList() {
  const [status, setStatus] = useState('');
  const [estagioFunil, setEstagioFunil] = useState('');
  const [search, setSearch] = useState('');

  const { data: parceiros, isLoading } = useQuery({
    queryKey: ['parceiros', status, estagioFunil, search],
    queryFn: () => api.get('/parceiros', {
      params: { status: status || undefined, estagio_funil: estagioFunil || undefined, search: search || undefined },
    }).then(r => r.data.data),
    refetchInterval: 15000,
  });

  const { data: funil } = useQuery({
    queryKey: ['parceiros-funil'],
    queryFn: () => api.get('/parceiros/funil').then(r => r.data.data),
    refetchInterval: 15000,
  });

  const funilChart = (funil || []).map(f => ({ ...f, label: FUNIL_LABELS[f.etapa] || f.etapa }));
  const maxTotal = Math.max(1, ...funilChart.filter(f => f.disponivel).map(f => f.total || 0));

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-800">Parceiros</h1>
      </div>

      <div className="bg-white rounded-xl shadow p-6">
        <h2 className="text-lg font-semibold text-gray-700 mb-4">Funil da Rede de Parceiros</h2>
        <div className="space-y-3">
          {funilChart.map(f => (
            <div key={f.etapa} className="flex items-center gap-3">
              <div className="w-28 shrink-0 text-sm text-gray-600 flex items-center gap-1">
                {f.label}
                {FUNIL_NOTES[f.etapa] && (
                  <span className="group relative inline-flex">
                    <HelpCircle size={13} className="text-gray-400 cursor-help" />
                    <span className="pointer-events-none absolute left-1/2 bottom-full z-10 mb-1 w-56 -translate-x-1/2 rounded-md bg-gray-800 px-2 py-1.5 text-xs text-white opacity-0 shadow-lg transition-opacity group-hover:opacity-100">
                      {FUNIL_NOTES[f.etapa]}
                    </span>
                  </span>
                )}
              </div>
              <div className="flex-1 flex items-center gap-2">
                <div className="flex-1 h-6 bg-gray-100 rounded overflow-hidden">
                  <div
                    className="h-full bg-[#1B3F5E] rounded transition-all"
                    style={{ width: `${Math.max(4, ((f.total || 0) / maxTotal) * 100)}%` }}
                  />
                </div>
                <span className="w-10 text-right text-sm font-medium text-gray-700">{f.total}</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="flex items-center gap-3">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-3 text-gray-400" />
          <input
            className="input-field pl-10"
            placeholder="Buscar por nome ou imobiliária..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          {search && <button onClick={() => setSearch('')} className="absolute right-3 top-3 text-gray-400"><X size={16} /></button>}
        </div>
        <select className="input-field w-48" value={status} onChange={e => setStatus(e.target.value)}>
          <option value="">Todos os status</option>
          <option value="ativo">Ativo</option>
          <option value="morno">Morno</option>
          <option value="parado">Parado</option>
          <option value="novo">Novo</option>
        </select>
        <select className="input-field w-56" value={estagioFunil} onChange={e => setEstagioFunil(e.target.value)}>
          <option value="">Todos os estágios do funil</option>
          {Object.entries(ESTAGIO_FUNIL_LABELS).map(([key, label]) => (
            <option key={key} value={key}>{label}</option>
          ))}
        </select>
      </div>

      {isLoading ? (
        <div className="text-center text-gray-400 py-12">Carregando...</div>
      ) : (
        <div className="bg-white rounded-xl shadow overflow-x-auto">
          <table className="w-full text-sm min-w-[1120px]">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="text-left px-3 py-3 font-semibold text-gray-500 text-xs uppercase">#</th>
                <th className="text-left px-3 py-3 font-semibold text-gray-500 text-xs uppercase">Nome</th>
                <th className="text-left px-3 py-3 font-semibold text-gray-500 text-xs uppercase">Imobiliária</th>
                <th className="text-left px-3 py-3 font-semibold text-gray-500 text-xs uppercase">Status</th>
                <th className="text-right px-3 py-3 font-semibold text-gray-500 text-xs uppercase" title="logins×1 + visualizações×2 + simulações×5 + downloads×3 + inscrição em evento×5 + comparecimento em evento×20 − falta em evento×3 + propostas×15 + vendas×50">Pontuação</th>
                <th className="text-right px-3 py-3 font-semibold text-gray-500 text-xs uppercase">Logins</th>
                <th className="text-right px-3 py-3 font-semibold text-gray-500 text-xs uppercase">Simulações</th>
                <th className="text-right px-3 py-3 font-semibold text-gray-500 text-xs uppercase">Downloads</th>
                <th className="text-right px-3 py-3 font-semibold text-gray-500 text-xs uppercase" title="Eventos em que se inscreveu (entre parênteses, faltas: confirmou e não compareceu)">Ev. inscr.</th>
                <th className="text-right px-3 py-3 font-semibold text-gray-500 text-xs uppercase" title="Eventos em que compareceu (check-in na portaria)">Ev. compar.</th>
                <th className="text-right px-3 py-3 font-semibold text-gray-500 text-xs uppercase">Propostas</th>
                <th className="text-right px-3 py-3 font-semibold text-gray-500 text-xs uppercase">Vendas</th>
                <th className="text-left px-3 py-3 font-semibold text-gray-500 text-xs uppercase">Última interação</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {(parceiros || []).map((p, idx) => (
                <tr key={p.id} className="hover:bg-gray-50">
                  <td className="px-3 py-3 text-gray-400 font-medium">{idx + 1}º</td>
                  <td className="px-3 py-3 font-medium">
                    <Link to={`/vendas/parceiros/${p.id}`} className="text-primary hover:underline">{p.nome}</Link>
                  </td>
                  <td className="px-3 py-3 text-gray-500">{p.imobiliaria_nome || '—'}</td>
                  <td className="px-3 py-3">
                    {p.status_cadastro === 'pendente'
                      ? <span className="px-2 py-1 rounded-full text-xs font-semibold bg-yellow-100 text-yellow-800">Pendente de aprovação</span>
                      : <Badge label={STATUS_LABELS[p.status_engajamento] || p.status_engajamento} variant={p.status_engajamento} />}
                  </td>
                  <td className="px-3 py-3 text-right font-bold text-primary">{p.pontuacao}</td>
                  <td className="px-3 py-3 text-right text-gray-500">{p.total_logins}</td>
                  <td className="px-3 py-3 text-right text-gray-500">{p.total_simulacoes}</td>
                  <td className="px-3 py-3 text-right text-gray-500">{p.total_downloads}</td>
                  <td className="px-3 py-3 text-right text-gray-500">
                    {p.total_eventos_inscrito || 0}
                    {Number(p.total_eventos_faltou) > 0 && (
                      <span className="text-red-500" title="faltas (confirmou e não compareceu)"> (−{p.total_eventos_faltou})</span>
                    )}
                  </td>
                  <td className="px-3 py-3 text-right font-medium text-gray-700">{p.total_eventos_compareceu || 0}</td>
                  <td className="px-3 py-3 text-right text-gray-500">{p.total_propostas}</td>
                  <td className="px-3 py-3 text-right text-gray-500">{p.total_vendas}</td>
                  <td className="px-3 py-3 text-gray-500 whitespace-nowrap">
                    {p.last_interaction_at
                      ? formatDistanceToNow(new Date(p.last_interaction_at), { locale: ptBR, addSuffix: true })
                      : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {(parceiros || []).length === 0 && <div className="text-center text-gray-400 py-8">Nenhum parceiro encontrado.</div>}
        </div>
      )}
    </div>
  );
}
